// @ts-nocheck
import { FC, useEffect, useRef, memo } from 'react';
import { colors, fontWeights, fonts, caption, button } from 'ui/theme';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { transformDataforSeriesGraph } from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/utils';
import { formatAsShortDate } from 'modules/intelligentInsights/domain.utils';
import {
  FroInsightsData,
  PerformanceOverTimeLift,
} from 'modules/intelligentInsights/frequencyOptimization/types';

export type PerformanceOverTimeGraphProps = {
  froInsightsData: FroInsightsData;
  selectedLiftTab: PerformanceOverTimeLift;
};
export const PerformanceOverTimeGraph: FC<PerformanceOverTimeGraphProps> = memo(
  ({ froInsightsData, selectedLiftTab }) => {
    const chartComponentRef = useRef(null);
    const sampleTransformedData = transformDataforSeriesGraph(
      froInsightsData.messageMediumByTimeData,
      selectedLiftTab,
    );
    const yAxisLabel = `${selectedLiftTab} rate`;

    // Handle window resize event to trigger Highcharts reflow
    useEffect(() => {
      const handleResize = () => {
        if (chartComponentRef.current) {
          const { chart } = chartComponentRef.current;
          if (chart.container && window.innerWidth < 1200) {
            chart.setSize(window.innerWidth - 100, null);
          } else {
            chart.setSize('600', null);
          }
        }
      };

      // Add event listener for window resize
      window.addEventListener('resize', handleResize);

      // Cleanup event listener on component unmount
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    return (
      <HighchartsReact
        ref={chartComponentRef}
        options={{
          reflow: true,
          exporting: {
            enabled: false,
          },
          chart: {
            // Specific height is to account for padding. 340 - 24
            height: 316,
            spacing: [10, 0, 0, 0],
          },
          credits: {
            enabled: false,
          },
          title: {
            text: '',
          },
          yAxis: {
            title: {
              text: yAxisLabel,
              style: {
                ...caption,
                fontFamily: fonts.sansSerif,
                color: colors.grey50,
              },
              margin: 16,
            },
            gridLineDashStyle: 'ShortDash',
            lineColor: colors.borderPrimary,
            lineWidth: 1,
            labels: {
              formatter: function () {
                return Highcharts.numberFormat(this.value * 100, 2) + '%'; // Convert decimal to percentage and show two decimal places
              },
              style: {
                ...button,
                color: colors.textSecondary,
                fontWeight: fontWeights.medium,
              },
            },
            min: 0,
          },
          xAxis: {
            type: 'datetime',
            tickPositions: sampleTransformedData[0]?.data.map((point) => point.x),
            lineColor: colors.borderPrimary,
            lineWidth: 1,
            labels: {
              style: {
                ...caption,
                fontFamily: fonts.sansSerif,
                color: colors.textSecondary,
              },
              formatter: function () {
                return formatAsShortDate(this.value);
              },
            },
          },
          tooltip: {
            borderColor: colors.grey80,
            borderRadius: 4,
            backgroundColor: colors.grey80,
            style: {
              fontFamily: fonts.sansSerif,
            },
            useHTML: true,
            formatter: function formatter() {
              return `<div style="color:${colors.grey25}">
                                ${this.point.date} 
                               <br>
                                <span style="
                                    display: inline-block;
                                    width: 10px;
                                    height: 10px;
                                    border-radius: 50%;
                                    background-color: ${this.series.color};
                                    margin-right: 5px;
                                    border: 1px solid white;
                                ">
                                </span> ${this.series.name}: ${Highcharts.numberFormat(this.y * 100, 2) + '%'} 
                               
                               </div>`;
            },
          },
          plotOptions: {
            series: {
              label: {
                connectorAllowed: true,
              },
              marker: {
                enabled: true,
                symbol: 'circle',
              },
            },
          },
          series: sampleTransformedData,
          legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            itemStyle: {
              ...caption,
              color: colors.textSecondary,
              fontWeight: fontWeights.medium,
              fontFamily: fonts.sansSerif,
            },
            labelFormatter: function () {
              return this.userOptions.name;
            },
            symbolWidth: 8,
            symbolHeight: 8,
            symbolRadius: 8,
            lineWidth: 0,
            itemMarginBottom: 16,
          },
        }}
        containerProps={{ style: { width: '100%', height: '100%' } }}
      />
    );
  },
);
